import { toast } from 'react-hot-toast';
import React, { useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Grid,
    TextField,
    Typography,
    Button,
    IconButton,
    Box,
    Autocomplete
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {getAllSalesTargetsAction,updateSalesTargetAction} from "../../../store/actions"
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import PropTypes from "prop-types";

// Popup dialog for updating farmer quantities
const FarmerTarget = ({ open, onClose, onSubmit, editRow }) => {

    const dispatch = useDispatch();

    // Formik form state
    const formik = useFormik({
        initialValues: {
            target_id: "",
            total_sales_target_qty: "",
            avg_qty_per_acre: "",
            avg_farmer_purch_qty: "",
        },
        validate: (values) => {
            const errors = {};
            if (!values.target_id) errors.target_id = "Required";
            if (!values.avg_qty_per_acre) errors.avg_qty_per_acre = "Required";
            if (!values.avg_farmer_purch_qty) errors.avg_farmer_purch_qty = "Required";
            return errors;
        },
        onSubmit: (values) => {
            if (!values.target_id) return;

            const payload = {
                endPoint: `target/sales/${values.target_id}`,
                total_sales_target_qty: values.total_sales_target_qty,
                avg_qty_per_acre: values.avg_qty_per_acre,
                avg_farmer_purch_qty: values.avg_farmer_purch_qty,
            };

            dispatch(updateSalesTargetAction(payload));
            toast.success('Sales target updated successfully!');
            onSubmit?.(payload);
            onClose?.();
        },
    });

    // Prefill formik values when editing
    React.useEffect(() => {
        if (open && editRow) {
            formik.setValues({
                target_id: String(editRow.id || ""),
                total_sales_target_qty: editRow.total_sales_target_qty || "",
                avg_qty_per_acre: editRow.avg_qty_per_acre || "",
                avg_farmer_purch_qty: editRow.avg_farmer_purch_qty || "",
            });
        } else if (open && !editRow) {
            formik.setValues({
                target_id: "",
                total_sales_target_qty: "",
                avg_qty_per_acre: "",
                avg_farmer_purch_qty: "",
            });
        }
    }, [open, editRow]);
    // Autofill total_sales_target_qty when sales target changes
    const { successData: salesData } = useSelector(state => state.getAllSalesTargetsReducer);
    // Flatten sales targets from API structure
    const salesTargets = React.useMemo(() => {
        if (!salesData?.data) return [];
        const result = [];
        salesData.data.forEach(soItem => {
            (soItem.districts || []).forEach(district => {
                // District-level sales targets
                (district.sales_targets || []).forEach(districtTarget => {
                    result.push(districtTarget);
                });
                // Mandal-level sales targets (if any)
                (district.mandals || []).forEach(mandalObj => {
                    (mandalObj.sales_targets || []).forEach(mandalTarget => {
                        result.push(mandalTarget);
                    });
                });
            });
        });
        return result;
    }, [salesData]);
    // Auto-fill all fields when a target is selected
    React.useEffect(() => {
        if (formik.values.target_id && salesTargets.length > 0) {
            const selected = salesTargets.find(t => String(t.id) === String(formik.values.target_id));
            if (selected) {
                formik.setFieldValue('total_sales_target_qty', selected.total_sales_target_qty || "");
                formik.setFieldValue('avg_qty_per_acre', selected.avg_qty_per_acre || "");
                formik.setFieldValue('avg_farmer_purch_qty', selected.avg_farmer_purch_qty || "");
            } else {
                formik.setFieldValue('total_sales_target_qty', "");
                formik.setFieldValue('avg_qty_per_acre', "");
                formik.setFieldValue('avg_farmer_purch_qty', "");
            }
        }
    }, [formik.values.target_id, salesTargets]);
    
    // Optionally set a default when data loads (keeps previous UX intent)
    useEffect(() => {
        if (!formik.values.target_id && salesTargets.length > 0) {
            // Keep empty to force user selection; set first id if desired
            // formik.setFieldValue('target_id', String(salesTargets[0].id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salesTargets]);

    useEffect(() => {
     dispatch(getAllSalesTargetsAction())
    }, [dispatch])

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 24 }}>
                Sales Update
                <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box component="form" onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item size={{xs:12}}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Sales Target</Typography>
                            <Autocomplete
                                fullWidth
                                options={salesTargets}
                                getOptionLabel={(item) => {
                                    if (!item) return "";
                                    const areaName = item.target_area?.zone_name
                                        || item.target_area?.region_name
                                        || item.target_area?.district_name
                                        || item.target_area?.name
                                        || String(item.target_area_id);
                                    const seasonLabel = item.season ? `${item.season.name} (${item.season.year})` : `Season ${item.season_id}`;
                                    return `${seasonLabel} • ${item.target_area_type}: ${areaName}`;
                                }}
                                value={salesTargets.find(t => String(t.id) === String(formik.values.target_id)) || null}
                                onChange={(_, value) => {
                                    formik.setFieldValue('target_id', value ? String(value.id) : '');
                                }}
                                onBlur={formik.handleBlur}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        name="target_id"
                                        placeholder="Select a sales target"
                                        error={formik.touched.target_id && Boolean(formik.errors.target_id)}
                                        helperText={formik.touched.target_id && formik.errors.target_id}
                                        sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                    />
                                )}
                                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                            />
                        </Grid>

                        <Grid item size={{xs:12}}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Total Sales Target</Typography>
                            <TextField
                                fullWidth
                                name="total_sales_target_qty"
                                value={formik.values.total_sales_target_qty}
                                slotProps={{ input: { readOnly: true } }}
                                sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                            />
                        </Grid>
                        <Grid item size={{xs:12}}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Avg Quantity Per Acre</Typography>
                            <TextField
                                fullWidth
                                name="avg_qty_per_acre"
                                value={formik.values.avg_qty_per_acre}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.touched.avg_qty_per_acre && formik.errors.avg_qty_per_acre)}
                                helperText={formik.touched.avg_qty_per_acre && formik.errors.avg_qty_per_acre}
                                sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                            />
                        </Grid>
                        <Grid item size={{xs:12}}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Avg Purchase Quantity</Typography>
                            <TextField
                                fullWidth
                                name="avg_farmer_purch_qty"
                                value={formik.values.avg_farmer_purch_qty}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.touched.avg_farmer_purch_qty && formik.errors.avg_farmer_purch_qty)}
                                helperText={formik.touched.avg_farmer_purch_qty && formik.errors.avg_farmer_purch_qty}
                                sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                            />
                        </Grid>

                        <Grid item size={{xs:6,}}>
                            <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}>
                                <Button type="submit" variant="contained" sx={{ px: 4, py: 1.2, borderRadius: 2, boxShadow: 3 }}>
                                    Submit Data
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

FarmerTarget.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    editRow: PropTypes.object
};

export default FarmerTarget;